import React, { useMemo } from "react";
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { styled } from "@mui/material/styles";
import moment, { Moment } from "moment";
import {
  dateSelected,
  setSelectedGroup,
} from "../../../redux/slice/bookingCreation/bookingCreationSlice";

import { RootState } from "../../../redux/store";
import { Assets } from "../../../assets/assets";
import BookingDatePicker from "../common/components/OrderDataStep/BookingDatePicker";

const StyledSelect = styled(Select)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    height: "62px",
    "& fieldset": {
      borderColor: theme.palette.primary.main,
      borderWidth: "1px",
    },
    "&:hover fieldset": {
      borderColor: theme.palette.primary.main,
      borderWidth: "1px",
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.input.focus,
      borderWidth: "1px",
    },
  },
  "&.Mui-focused .MuiOutlinedInput-root fieldset": {
    borderColor: theme.palette.input.focus,
    borderWidth: "1px",
  },
  "& .MuiInputBase-input": {
    color: theme.palette.primary.contrastText,
  },
}));

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    height: "62px",
    "& fieldset": {
      borderColor: theme.palette.primary.main,
      borderWidth: "1px",
    },
    "&:hover fieldset": {
      borderColor: theme.palette.primary.main,
      borderWidth: "1px",
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.palette.input.focus,
      borderWidth: "1px",
    },
  },
  "&.Mui-focused .MuiOutlinedInput-root fieldset": {
    borderColor: theme.palette.input.focus,
    borderWidth: "1px",
  },
  "& .MuiInputBase-input": {
    color: theme.palette.primary.contrastText,
  },
}));

const styles = {
  root: {
    padding: "2px",
    paddingTop: 0,
  },
  dateTimeWrapper: {
    display: "flex",
    flexDirection: "column",
    
  },
  inputWrapper: {
    width: "350px",
    display: "flex",
    flexDirection: "column",
    margin: `12px auto 0 auto`,
    "@media (min-width: 1280px)": {
      width: "350px",
    },
  },
};
const PackageDetailsStep = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const InfoIconWhiteFilled = Assets.icon.infoIconWhiteFilled.component;
  const { selectedLocation, selectedGroup } = useSelector(
    (state: RootState) => state.bookingCreation
  );

  const orderData = useSelector(
    (state: RootState) => state.bookingCreation.orderData
  );
  
  // Generate available dates without past dates in "DD.MM.YYYY" format
  const availableDates = useMemo(() => {
    const dates = [];
    const today = moment();
    // Generate dates for the next 60 days (you can adjust this range as needed)
    for (let i = 0; i < 60; i++) {
      const date = today.clone().add(i, 'days');
      dates.push(date.format('DD.MM.YYYY')); // Changed to DD.MM.YYYY format
    }
    return dates;
  }, []);

  const groupsByLocation = useSelector(
    (state: RootState) => state.bookingCreation.groupsByLocation
  );

  const storages = useMemo(() => {
    return selectedLocation ? groupsByLocation[selectedLocation.id] || [] : [];
  }, [selectedLocation, groupsByLocation]);

  const handlePackageSizeChange = (itemId: string) => {
    const storage = storages.find((storage) => storage.id === itemId);
    if (storage) {
      dispatch(setSelectedGroup(storage));
    }
  };
  const handleDateChange = (date: Moment | null) => {
    dispatch(dateSelected({ date }));
  };
  
  // Function to disable past dates - handle null case
  const shouldDisableDate = (date: Moment | null) => {
    if (!date) return true; // Disable if date is null
    return date.isBefore(moment(), 'day');
  };

  return (
    <Box sx={{ padding: "20px" }}>
      <Box sx={{ maxWidth: "400px", margin: "0 auto" }}>
        <StyledFormControl fullWidth sx={{ marginBottom: "20px" }}>
          <InputLabel>
            {t(
              "book_location.package_details.size_label",
              "Select package size"
            )}
          </InputLabel>
          <StyledSelect
            value={selectedGroup?.id}
            label={t(
              "book_location.package_details.size_label",
              "Select package size"
            )}
            onChange={(e) => {
              handlePackageSizeChange(e.target.value as string);
            }}
          >
            {storages.map((storage) => (
              <MenuItem key={storage.id} value={storage.id}>
                {storage.name}
              </MenuItem>
            ))}
          </StyledSelect>
        </StyledFormControl>
        {selectedGroup && (
          <Box sx={{ display: "flex", paddingBottom: 3 }}>
            <Box sx={{ marginRight: "5px", color: "#1E1E1E" }}>
              <InfoIconWhiteFilled
                style={{
                  width: "20px",
                  height: "20px",
                  // color: '#1E1E1E'
                }}
              />
            </Box>
            <Typography variant="body2" fontWeight="bold">
              {selectedGroup?.description}
            </Typography>
          </Box>
        )}

        <Box sx={styles.dateTimeWrapper}>
          <BookingDatePicker
            value={orderData.date}
            onChange={(date) => handleDateChange(date)}
            shouldDisableDate={shouldDisableDate}
            availableDates={availableDates}
            label={t("book_location.package_details.date_label")}
          />
        </Box>

        <Box
          sx={{
            backgroundColor: "#F5F5F5",
            padding: "12px",
            borderRadius: "4px",
            marginBottom: "20px",
          }}
        >
          <Typography variant="body2" sx={{ color: "#666" }}>
            <strong>
              {t("book_location.package_details.note_label", "NB")}:{" "}
            </strong>
            {t(
              "book_location.package_details.note",
              "No need to worry if you don't already have the above information. You can skip as the final selection will be done by the delivery agent."
            )}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default PackageDetailsStep;